// Label of a dimension or a force, as in the lock calculator (port of its QuoteLabel): WebGL text in a billboard on
// an opaque chip sized on the text troika measures. It is part of the scene like the rest of the dimension, so what
// stands in front of it hides it. With `awayFrom` the chip is moved, every frame, entirely to that side of `position`
// on screen, whatever the view.
import { Billboard, Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { memo, useCallback, useRef, useState, type FC } from "react";
import * as THREE from "three";
import { LABEL_FONT_URL, QUOTE, QUOTE_COLORS } from "./viewerConfig";

export type QuoteLabelProps = {
  // centre of the text, unless moved by awayFrom
  position: THREE.Vector3 | [number, number, number];
  text: string;
  textColor?: THREE.Color;
  backgroundColor?: THREE.Color;
  // text height (m)
  size?: number;
  // world direction the label stays beside `position` (the outer side of a dimension line)
  awayFrom?: THREE.Vector3;
};

// Background chip, sized and centred, in the billboard's local coordinates.
type ChipBox = { width: number; height: number; centerX: number; centerY: number };

const QuoteLabel: FC<QuoteLabelProps> = ({
  position,
  text,
  textColor = QUOTE_COLORS.labelText,
  backgroundColor = QUOTE_COLORS.labelBackground,
  size = QUOTE.labelSize,
  awayFrom,
}) => {
  const [chip, setChip] = useState<ChipBox | null>(null);
  const shiftRef = useRef<THREE.Group>(null);

  const handleSync = useCallback((mesh: unknown) => setChip((previous) => nextChip(previous, mesh, size)), [size]);

  // The billboard copies the camera's orientation: in its local frame X is the screen's right and Y its up.
  useFrame(({ camera }) => {
    const group = shiftRef.current;
    if (!group) return;
    const shift = awayFrom && chip ? labelShift(awayFrom, chip, camera.quaternion) : null;
    if (shift) group.position.set(shift[0], shift[1], 0);
    else group.position.set(0, 0, 0);
  });

  return (
    <Billboard position={position}>
      <group ref={shiftRef}>
        {chip ? (
          <mesh position={[chip.centerX, chip.centerY, -QUOTE.labelChipDepth]}>
            <planeGeometry args={[chip.width, chip.height]} />
            <meshBasicMaterial color={backgroundColor} />
          </mesh>
        ) : null}
        <Text
          font={LABEL_FONT_URL}
          fontSize={size}
          color={textColor}
          anchorX="center"
          anchorY="middle"
          onSync={handleSync}
        >
          {text}
        </Text>
      </group>
    </Billboard>
  );
};

// Below this on-screen component the direction runs along the view axis: no shift.
const MIN_SCREEN_COMPONENT = 0.2;
const SCREEN_RIGHT = new THREE.Vector3();
const SCREEN_UP = new THREE.Vector3();

// Shift (billboard's local frame) that puts the chip entirely on the `awayFrom` side of `position`, touching it: the
// rectangle's support distance in the on-screen direction (|dx|·w/2 + |dy|·h/2), plus re-centring on the text block.
function labelShift(
  awayFrom: THREE.Vector3,
  chip: ChipBox,
  cameraQuaternion: THREE.Quaternion,
): [number, number] | null {
  SCREEN_RIGHT.set(1, 0, 0).applyQuaternion(cameraQuaternion);
  SCREEN_UP.set(0, 1, 0).applyQuaternion(cameraQuaternion);
  let dx = awayFrom.dot(SCREEN_RIGHT);
  let dy = awayFrom.dot(SCREEN_UP);
  const length = Math.hypot(dx, dy);
  if (length < MIN_SCREEN_COMPONENT) return null;
  dx /= length;
  dy /= length;
  const distance = (Math.abs(dx) * chip.width) / 2 + (Math.abs(dy) * chip.height) / 2;
  return [dx * distance - chip.centerX, dy * distance - chip.centerY];
}

// Chip from troika's blockBounds ([minX, minY, maxX, maxY], local, after the anchors). Returns the previous value
// when nothing changed: onSync fires on every re-sync and a new object would re-create the plane for nothing.
function nextChip(previous: ChipBox | null, mesh: unknown, size: number): ChipBox | null {
  const bounds = (mesh as { textRenderInfo?: { blockBounds?: ArrayLike<number> } } | null)?.textRenderInfo
    ?.blockBounds;
  if (!bounds || bounds.length < 4) return previous;
  const minX = bounds[0] ?? 0;
  const minY = bounds[1] ?? 0;
  const maxX = bounds[2] ?? 0;
  const maxY = bounds[3] ?? 0;
  const padding = size * QUOTE.labelChipPaddingRatio;
  const next: ChipBox = {
    width: maxX - minX + 2 * padding,
    height: maxY - minY + 2 * padding,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
  if (next.width <= 0 || next.height <= 0) return previous;
  return previous && sameChip(previous, next) ? previous : next;
}

// a micron: troika's numeric noise must not re-render
const CHIP_EPSILON = 1e-6;

function sameChip(a: ChipBox, b: ChipBox): boolean {
  return (
    Math.abs(a.width - b.width) < CHIP_EPSILON &&
    Math.abs(a.height - b.height) < CHIP_EPSILON &&
    Math.abs(a.centerX - b.centerX) < CHIP_EPSILON &&
    Math.abs(a.centerY - b.centerY) < CHIP_EPSILON
  );
}

export default memo(QuoteLabel);
